/* eslint-disable react/jsx-props-no-spreading */
import React from 'react'
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ChartLineIcon } from '@rsmus/myrsm-icons'
import { tokens } from 'src/styles/materialTheme'
import ResponsiveModal from '../../rsmCoreComponents/components/ResponsiveModal'
import { Styles } from '../../types'

interface ConfirmRemoveReportDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  reportDisplayName?: string
  reportShortName?: string
}

const styles: Styles = {
  iconCell: {
    display: 'flex',
    justifyContent: 'center',
    border: 0,
    paddingTop: '1.5rem',
  },
  title: {
    fontFamily: 'Prelo, sans-serif',
    color: tokens.colors.rsmGray.copy,
    fontWeight: 300,
    fontSize: '2.25rem',
    lineHeight: '2.5rem',
    letterSpacing: 0,
    textAlign: 'center',
    border: 0,
    padding: '0.5rem',
  },

  subtitle: {
    fontFamily: 'Prelo, sans-serif',
    fontWeight: 400,
    fontSize: '1rem',
    lineHeight: '1.5rem',
    letterSpacing: 0,
    color: 'text.secondary',
    textAlign: 'center',
    padding: '0.5rem',
    border: 0,
  },

  names: {
    fontFamily: 'Prelo, sans-serif',
    fontWeight: 700,
    fontSize: '1rem',
    lineHeight: '1.5rem',
    letterSpacing: 0,
    textAlign: 'center',
    padding: '0.5rem',
    border: 0,
  },
  actionsCell: {
    border: 0,
    paddingTop: '2rem',
    paddingBottom: '1rem',
  },
  actions: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '0.625rem',
  },
  iconWrap: {
    width: '7.5rem',
    height: '7.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      width: '7.5rem',
      height: '7.5rem',
    },
  },
}
const ConfirmRemoveReportDialog = ({
  open,
  onClose,
  onConfirm,
  reportDisplayName,
  reportShortName,
}: ConfirmRemoveReportDialogProps) => {
  const { t } = useTranslation()
  const desktopMaxWidth = '41rem'
  const desktopMaxHeight = '30rem'

  return (
    <Box className="Rsm-ProfileModal">
      <ResponsiveModal
        open={open}
        onClose={onClose}
        aria-label={t('PowerBI.RemoveReportModal.Title')}
        desktopMaxHeight={desktopMaxHeight}
        desktopMaxWidth={desktopMaxWidth}
        removeHeaderSpacing
        data-testid="Lbl_PowerBI_RemoveDialog_Modal">
        <Table>
          <TableBody>
            <TableRow>
              <TableCell sx={styles.iconCell}>
                <Box sx={styles.iconWrap}>
                  <ChartLineIcon />
                </Box>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                sx={styles.title}
                data-testid="Tbl_PowerBI_RemoveDialog_Title">
                {t('PowerBI.RemoveReportModal.Header')}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                sx={styles.subtitle}
                data-testid="Tbl_PowerBI_RemoveDialog_Subtitle">
                {t('PowerBI.RemoveReportModal.SubHeader')}
              </TableCell>
            </TableRow>

            {(reportDisplayName || reportShortName) && (
              <TableRow>
                <TableCell
                  sx={styles.names}
                  data-testid="Tbl_PowerBI_RemoveDialog_ReportNames">
                  {reportDisplayName} – {reportShortName}
                </TableCell>
              </TableRow>
            )}

            <TableRow>
              <TableCell sx={styles.actionsCell}>
                <Stack direction="row" sx={styles.actions}>
                  <Button
                    variant="outlined"
                    onClick={onClose}
                    data-testid="Btn_PowerBI_RemoveDialog_Cancel">
                    {t('PowerBI.RemoveReportModal.Cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={onConfirm}
                    data-testid="Btn_PowerBI_RemoveDialog_Confirm">
                    {t('PowerBI.RemoveReportModal.Confirm')}
                  </Button>
                </Stack>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </ResponsiveModal>
    </Box>
  )
}

export default ConfirmRemoveReportDialog
